import React, { useEffect, useState, useCallback } from 'react';
import { SectionTitle } from './PageShell';
import AdminShell from './AdminShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Building2, Hash, Users, Trash2, Loader2, Check, Clock,
  MessageSquare, Sparkles, ShieldCheck, Archive, Inbox, ArrowRight,
} from 'lucide-react';

const TYPE_META = {
  eleve: { label: 'Question élève', Icon: Users, color: 'text-sky-400 bg-sky-500/15 border-sky-500/40' },
  ecsr_verification: { label: 'Enseignant ECSR', Icon: ShieldCheck, color: 'text-red-400 bg-red-500/15 border-red-500/40' },
  demo: { label: 'Demande démo', Icon: Sparkles, color: 'text-amber-400 bg-amber-500/15 border-amber-500/40' },
  partnership: { label: 'Partenariat auto-école', Icon: Building2, color: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/40' },
  other: { label: 'Autre', Icon: MessageSquare, color: 'text-white/70 bg-white/5 border-white/20' },
};

const STATUS_META = {
  new: { label: 'Nouvelle', color: 'text-red-300 bg-red-500/15 border-red-500/40' },
  contacted: { label: 'Contactée', color: 'text-amber-300 bg-amber-500/15 border-amber-500/40' },
  converted: { label: 'Convertie', color: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40' },
  archived: { label: 'Archivée', color: 'text-white/50 bg-white/5 border-white/15' },
};

export default function AdminDemandesPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [replyForId, setReplyForId] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyResult, setReplyResult] = useState(null); // {id, ok, dry_run, error}

  const openReply = (it) => {
    setReplyForId(it.id);
    setReplySubject(`Re : ta demande sur codevoiture.fr`);
    setReplyMessage(`Bonjour ${it.full_name.split(' ')[0]},\n\n\n\nÀ bientôt sur la route,\nBruno — Code Voiture\ncontact@codevoiture.fr`);
    setReplyResult(null);
  };

  const sendReply = async (id) => {
    setReplySending(true);
    setReplyResult(null);
    try {
      const r = await api.adminDemoRequestReply(id, { subject: replySubject, message: replyMessage });
      setReplyResult({ id, ok: r.ok, dry_run: r.delivery?.dry_run, error: r.delivery?.error });
      if (r.ok) { setReplyForId(null); await refresh(); }
    } catch (e) {
      setReplyResult({ id, ok: false, error: e?.response?.data?.detail || 'Erreur d\'envoi' });
    }
    setReplySending(false);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminDemoRequestList(statusFilter || undefined);
      setItems(d.items || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { if (!authLoading && user?.is_admin) refresh(); }, [authLoading, user, refresh, statusFilter]);

  const setStatus = async (id, status) => {
    try { await api.adminDemoRequestUpdate(id, { status }); await refresh(); } catch (_) { /* silent */ }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    try { await api.adminDemoRequestDelete(id); await refresh(); } catch (_) { /* silent */ }
  };

  if (authLoading) return <AdminShell><div className="max-w-6xl mx-auto p-12 text-white/50">Chargement…</div></AdminShell>;
  if (!user?.is_admin) return <AdminShell><div className="max-w-3xl mx-auto p-12 text-center text-white/60">Accès réservé aux administrateurs.</div></AdminShell>;

  const counts = items.reduce((acc, it) => { acc[it.status] = (acc[it.status] || 0) + 1; return acc; }, {});

  return (
    <AdminShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8">
        <SectionTitle kicker="Admin · CRM leads" title="Demandes d'information" sub="Auto-écoles, moniteurs indépendants, partenariats — les leads reçus via le formulaire public." />

        {/* Filtres */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <button
            onClick={() => setStatusFilter('')}
            data-testid="admin-demandes-filter-all"
            className={`px-4 h-9 rounded-full text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${statusFilter === '' ? 'bg-white text-black' : 'bg-white/5 text-white/60 hover:text-white border border-white/10'}`}
          >
            <Inbox size={12} /> Toutes ({items.length})
          </button>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              data-testid={`admin-demandes-filter-${k}`}
              className={`px-4 h-9 rounded-full text-[12.5px] font-semibold border ${statusFilter === k ? m.color + ' opacity-100' : 'border-white/10 bg-white/[0.03] text-white/50 hover:text-white'}`}
            >
              {m.label} {counts[k] ? `(${counts[k]})` : ''}
            </button>
          ))}
          <Link to="/admin" className="ml-auto text-[13px] text-white/60 hover:text-white">← Dashboard admin</Link>
        </div>

        {loading ? (
          <div className="text-white/40 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Chargement…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-12 text-center">
            <Mail size={36} className="mx-auto text-white/30 mb-4" />
            <h3 className="font-serif-editorial text-white text-[22px]">Aucune demande pour le moment</h3>
            <p className="text-white/50 mt-2 text-[13.5px]">Les demandes envoyées via <Link to="/demande-info" className="text-red-400 hover:text-red-300 underline">/demande-info</Link> apparaîtront ici.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => {
              const tm = TYPE_META[it.request_type] || TYPE_META.other;
              const sm = STATUS_META[it.status] || STATUS_META.new;
              const Icon = tm.Icon;
              const isOpen = expandedId === it.id;
              return (
                <div key={it.id} className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden" data-testid={`admin-demande-${it.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : it.id)}
                    className="w-full flex items-center gap-4 p-4 sm:p-5 hover:bg-white/[0.02] transition text-left"
                  >
                    <div className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${tm.color}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold text-[15px] truncate">{it.full_name}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-widest ${sm.color}`}>{sm.label}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-widest ${tm.color}`}>{tm.label}</span>
                        {it.auto_reply_sent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-bold uppercase tracking-widest" title="Un accusé de réception automatique a été envoyé au demandeur">✓ Accusé auto</span>
                        )}
                      </div>
                      <div className="text-white/55 text-[12.5px] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1"><Mail size={11} /> {it.email}</span>
                        {it.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {it.phone}</span>}
                        {it.organization && <span className="inline-flex items-center gap-1"><Building2 size={11} /> {it.organization}</span>}
                        {it.city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {it.city}</span>}
                      </div>
                    </div>
                    <div className="text-white/40 text-[11px] font-mono-spec shrink-0 hidden sm:block">
                      {new Date(it.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-white/10 p-4 sm:p-6 space-y-4 bg-white/[0.015]">
                      <div className="grid sm:grid-cols-2 gap-4 text-[13px]">
                        {it.role_label && <div><span className="text-white/45">Rôle :</span> <span className="text-white">{it.role_label}</span></div>}
                        {it.ecsr_number && <div><span className="text-white/45">N° ECSR :</span> <span className="text-white font-mono-spec">{it.ecsr_number}</span></div>}
                        {it.team_size && <div><span className="text-white/45">Effectif :</span> <span className="text-white">{it.team_size}</span></div>}
                        <div><span className="text-white/45">Reçu :</span> <span className="text-white">{new Date(it.created_at).toLocaleString('fr-FR')}</span></div>
                      </div>
                      {it.message && (
                        <div className="p-4 rounded-xl border-l-2 border-red-500 bg-white/[0.02]">
                          <div className="text-[10.5px] uppercase tracking-widest text-white/40 font-bold mb-2">Message</div>
                          <div className="text-white/85 text-[14px] whitespace-pre-line leading-relaxed">{it.message}</div>
                        </div>
                      )}
                      {replyForId === it.id && (
                        <div className="rounded-xl border border-red-500/25 bg-red-950/10 p-4 space-y-3" data-testid={`admin-reply-panel-${it.id}`}>
                          <div className="text-[10.5px] uppercase tracking-widest text-red-300 font-bold">Réponse à {it.email} · envoyée depuis contact@codevoiture.fr</div>
                          <input
                            value={replySubject}
                            onChange={(e) => setReplySubject(e.target.value)}
                            data-testid="admin-reply-subject"
                            className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[13.5px]"
                            placeholder="Objet"
                          />
                          <textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            data-testid="admin-reply-message"
                            rows={6}
                            className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[13.5px] leading-relaxed resize-y"
                            placeholder="Ton message…"
                          />
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => sendReply(it.id)}
                              disabled={replySending || replyMessage.trim().length < 5}
                              data-testid="admin-reply-send"
                              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-[12.5px] font-semibold"
                            >
                              {replySending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Envoyer la réponse
                            </button>
                            <button onClick={() => setReplyForId(null)} className="h-9 px-3 rounded-full border border-white/10 text-white/60 text-[12px]">Annuler</button>
                          </div>
                          {replyResult && replyResult.id === it.id && !replyResult.ok && (
                            <div className="text-red-300 text-[12.5px]" data-testid="admin-reply-error">
                              ⚠️ Échec d&apos;envoi : {String(replyResult.error || '').includes('testing emails')
                                ? 'Resend est en mode test — valide le domaine codevoiture.fr sur resend.com/domains pour pouvoir répondre à tout le monde.'
                                : replyResult.error}
                            </div>
                          )}
                        </div>
                      )}
                      {(it.replies || []).length > 0 && (
                        <div className="space-y-2">
                          <div className="text-[10.5px] uppercase tracking-widest text-emerald-300/80 font-bold">Réponses envoyées ({it.replies.length})</div>
                          {it.replies.map((r, ri) => (
                            <div key={ri} className="p-3 rounded-lg border-l-2 border-emerald-500/50 bg-white/[0.02]">
                              <div className="text-white/85 text-[13px] font-semibold">{r.subject}</div>
                              <div className="text-white/60 text-[12.5px] whitespace-pre-line mt-1 line-clamp-3">{r.message}</div>
                              <div className="text-white/35 text-[11px] mt-1.5">{r.sent_at ? new Date(r.sent_at).toLocaleString('fr-FR') : ''}{r.delivery?.dry_run ? ' · simulation (clé Resend absente)' : r.delivery?.ok ? ' · délivré à Resend' : ' · ⚠️ non délivré'}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap pt-2">
                        <button
                          type="button"
                          onClick={() => (replyForId === it.id ? setReplyForId(null) : openReply(it))}
                          data-testid={`admin-demande-reply-${it.id}`}
                          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold"
                        >
                          <Mail size={13} /> Répondre depuis contact@codevoiture.fr
                        </button>
                        {it.phone && (
                          <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[12.5px] font-semibold">
                            <Phone size={13} /> Appeler
                          </a>
                        )}
                        <div className="w-px h-6 bg-white/10 mx-1" />
                        {Object.entries(STATUS_META).filter(([k]) => k !== it.status).map(([k, m]) => (
                          <button
                            key={k}
                            onClick={() => setStatus(it.id, k)}
                            data-testid={`admin-demande-status-${it.id}-${k}`}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[11.5px] font-semibold ${m.color} hover:opacity-80`}
                          >
                            {k === 'contacted' && <Clock size={11} />}
                            {k === 'converted' && <Check size={11} />}
                            {k === 'archived' && <Archive size={11} />}
                            {k === 'new' && <Inbox size={11} />}
                            {m.label}
                          </button>
                        ))}
                        <button
                          onClick={() => remove(it.id)}
                          data-testid={`admin-demande-delete-${it.id}`}
                          className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-white/10 text-white/50 hover:text-red-400 hover:border-red-500/40 text-[11.5px]"
                        >
                          <Trash2 size={11} /> Supprimer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </AdminShell>
  );
}
